(() => {
  // Keep existing branded document cards visible in Resources, including cards
  // left in a legacy content-truncator by a verified-description override.
  // Move the real node rather than cloning it, so links and alt text stay intact.
  const organizeManualDocuments = (productInfo, information) => {
    const destination = information?.querySelector('[data-amc-pi-destination="resources"]');
    if (!destination) return;
    const cards = Array.from(productInfo.querySelectorAll('.amc-doc-card'));
    cards.forEach((card) => {
      if (card.querySelector('form,script,style,template,product-form,product-info')) return;
      card.setAttribute('data-amc-resource-document', '');
      if (!destination.contains(card)) destination.appendChild(card);
    });
    if (cards.length) information.querySelector('[data-amc-pi-resources-help]')?.setAttribute('hidden', '');
  };
  const organizeInteractiveResources = (scope = document) => {
    const productInfos = scope.matches?.('product-info') ? [scope] : Array.from(scope.querySelectorAll?.('product-info') || []);
    productInfos.forEach((productInfo) => {
      const information = productInfo.querySelector('amc-product-information');
      const destination = information?.querySelector('[data-amc-pi-destination="resources"]');
      if (!destination) return;
      organizeManualDocuments(productInfo, information);
      const resourceGrids = Array.from(productInfo.querySelectorAll('[data-amc-product-configuration] .product__description > .amc-resource-grid'));
      resourceGrids.forEach((grid) => destination.appendChild(grid));
      if (resourceGrids.length) information.querySelector('[data-amc-pi-resources-help]')?.setAttribute('hidden', '');
    });
  };
  const queueProductOrganization = (scope = document) => requestAnimationFrame(() => {
    organizeInteractiveResources(scope);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => queueProductOrganization(), { once: true });
  else queueProductOrganization();
  window.addEventListener('load', () => queueProductOrganization(), { once: true });
  [100, 500, 1500, 4000].forEach((delay) => setTimeout(() => queueProductOrganization(), delay));
  document.addEventListener('shopify:section:load', (event) => queueProductOrganization(event.target));

  if (customElements.get('amc-product-information')) return;

  class AMCProductInformation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const rail = this.querySelector('[data-amc-pi-tabs]');
      if (!rail) return;
      this.initialized = true;
      this.rail = rail;
      this.movePurchaseResourcesAfterContent();
      this.moveInteractiveResourceGrid();
      this.moveCleanCopy();
      this.moveVerifiedSections();
      // 2026-10-16: drop the Dimensions tab when the product has no dimension data.
      ['dimensions', 'specifications'].forEach((kind) => {
        const panel = this.querySelector(`[data-amc-pi-panel="${kind}"]`);
        if (!panel) return;
        const migrated = panel.querySelector(`[data-amc-pi-destination="${kind}"]`);
        const hasData = panel.querySelector('table, dl, ul, [data-amc-pi-verified-dimensions]') || (migrated && migrated.children.length);
        if (!hasData) {
          rail.querySelector(`[data-amc-pi-tab="${kind}"]`)?.remove();
          panel.remove();
        }
      });
      this.tabs = Array.from(rail.querySelectorAll('[data-amc-pi-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-amc-pi-panel]'));
      rail.setAttribute('role', 'tablist');
      rail.setAttribute('aria-label', 'Product information');
      this.tabs.forEach((tab) => {
        const panel = this.panelFor(tab);
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        panel.setAttribute('role', 'tabpanel');
        panel.tabIndex = 0;
      });
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onHashchange = this.handleHash.bind(this);
      this.addEventListener('click', this.onClick);
      rail.addEventListener('keydown', this.onKeydown);
      window.addEventListener('hashchange', this.onHashchange);
      this.setAttribute('data-amc-pi-enhanced', '');
      this.activate(this.tabs[0]);
      this.handleHash();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.rail?.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('hashchange', this.onHashchange);
      this.initialized = false;
    }

    panelFor(tab) {
      return this.querySelector(`[data-amc-pi-panel="${tab.dataset.amcPiTab}"]`);
    }

    movePurchaseResourcesAfterContent() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      const resources = Array.from(productInfo.querySelectorAll('.amc-purchase-resources'));
      resources.forEach((resource) => {
        if (resource.parentElement === this.parentElement && resource.previousElementSibling === this) return;
        this.after(resource);
      });
    }

    moveInteractiveResourceGrid() {
      const productInfo = this.closest('product-info');
      const destination = this.querySelector('[data-amc-pi-destination="resources"]');
      if (!productInfo || !destination) return;
      organizeManualDocuments(productInfo, this);
      const resourceGrids = Array.from(productInfo.querySelectorAll('[data-amc-product-configuration] .product__description > .amc-resource-grid'));
      resourceGrids.forEach((grid) => destination.appendChild(grid));
      if (resourceGrids.length) this.querySelector('[data-amc-pi-resources-help]')?.setAttribute('hidden', '');
    }

    activate(tab, updateHash = false) {
      if (!tab) return;
      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        this.panelFor(item).hidden = !selected;
      });
      if (updateHash) history.replaceState(history.state, '', tab.getAttribute('href'));
    }

    handleKeydown(event) {
      const tab = event.target.closest('[data-amc-pi-tab]');
      const index = this.tabs.indexOf(tab);
      if (index < 0) return;
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.activate(tab, true);
        return;
      } else return;
      event.preventDefault();
      event.stopPropagation();
      this.tabs.forEach((item, position) => { item.tabIndex = position === next ? 0 : -1; });
      this.tabs[next].focus();
    }

    handleClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const href = link.getAttribute('href');
      if (!href || href.charAt(0) !== '#') return;
      const target = this.targetForHash(href);
      const panel = target?.closest('[data-amc-pi-panel]');
      if (!panel || !this.contains(panel)) return;
      // Legacy theme document-level hash scrolling ignores defaultPrevented.
      // Contain only this component's own navigation, not other product links.
      event.stopPropagation();
      const tab = this.tabs.find((item) => this.panelFor(item) === panel);
      if (link.matches('[data-amc-pi-tab]')) {
        event.preventDefault();
        this.activate(tab, true);
      } else {
        // Reveal first; the original anchor keeps its native URL/scroll behavior.
        this.activate(tab);
      }
    }

    targetForHash(hash) {
      try { return document.getElementById(decodeURIComponent(hash.slice(1))); }
      catch (_) { return null; }
    }

    handleHash() {
      const hash = location.hash;
      const target = this.targetForHash(hash);
      const panel = target?.closest('[data-amc-pi-panel]');
      if (!panel || !this.contains(panel)) return;
      let ancestor = target.parentElement;
      while (ancestor && ancestor !== this) {
        if (ancestor.tagName === 'DETAILS') ancestor.open = true;
        ancestor = ancestor.parentElement;
      }
      this.activate(this.tabs.find((tab) => this.panelFor(tab) === panel));
      // A deep-link scroll may have happened before the original nodes moved.
      // Reposition once after reveal; normal tab clicks do not call this path.
      requestAnimationFrame(() => {
        if (this.isConnected && location.hash === hash) target.scrollIntoView({ block: 'start', behavior: 'auto' });
      });
    }

    moveCleanCopy() {
      // Configurator stays above the tabs; the verified copy after the marker moves into Details.
      const config = document.getElementById(`${this.dataset.amcInformationId}-configuration`);
      const marker = config?.querySelector('[data-amc-copy-start]');
      const details = this.querySelector('[data-amc-pi-panel="details"]');
      if (!marker || !details || details.querySelector('.product__description')) return;
      const wrap = document.createElement('div');
      wrap.className = 'product__description rte amc-product-content amc-pi__copy';
      let node = marker.nextSibling;
      while (node) { const next = node.nextSibling; wrap.appendChild(node); node = next; }
      marker.remove();
      details.querySelectorAll(':scope > .amc-pi__note').forEach((note) => note.remove());
      details.appendChild(wrap);
    }

    moveVerifiedSections() {
      const description = this.querySelector('[data-amc-pi-panel="details"] .product__description');
      if (!description || description.dataset.amcPiOrganized) return;
      description.dataset.amcPiOrganized = 'true';
      const faqPattern = /^(?:faqs?|frequently asked questions)(?:\s|:|$)/i;
      const existingFAQ = Array.from(description.querySelectorAll('h2,h3,h4,summary')).some((heading) => faqPattern.test(heading.textContent.trim()));
      let faqLocationNote;
      if (existingFAQ) {
        this.querySelector('[data-amc-pi-generic-help]')?.setAttribute('hidden', '');
        faqLocationNote = document.createElement('p');
        faqLocationNote.className = 'amc-pi__note';
        const link = document.createElement('a');
        link.href = `#${this.dataset.amcInformationId}-details`;
        link.textContent = 'Read the product-specific questions in Product details';
        faqLocationNote.appendChild(link);
        this.querySelector('[data-amc-pi-destination="faqs"]').appendChild(faqLocationNote);
      }
      const groups = [];
      const unsafeSelector = 'form,script,style,template,product-form,product-info';
      const isSafe = (node) => node?.nodeType === 1 && !node.matches(unsafeSelector) && !node.querySelector(unsafeSelector);
      const sources = [description];
      Array.from(description.children).forEach((wrapper) => {
        if (!/^(DIV|SECTION|ARTICLE)$/.test(wrapper.tagName) || !isSafe(wrapper) || !wrapper.querySelector('h2,h3,h4,details>summary')) return;
        sources.push(wrapper);
      });

      const resourcesDestination = this.querySelector('[data-amc-pi-destination="resources"]');
      Array.from(description.children).forEach((node) => {
        const label = (node.textContent || '').replace(/\s+/g, ' ').trim();
        if (!resourcesDestination || !isSafe(node) || !/^(?:free downloads\b|downloads\b|manuals & startup guide\b)/i.test(label)) return;
        resourcesDestination.appendChild(node);
        this.querySelector('[data-amc-pi-resources-help]')?.setAttribute('hidden', '');
      });

      sources.forEach((source) => {
        Array.from(source.children).forEach((node) => {
          if (node.tagName !== 'DETAILS' || !isSafe(node)) return;
          const summary = node.querySelector(':scope > summary');
          if (!summary || !faqPattern.test(summary.textContent.trim())) return;
          this.querySelector('[data-amc-pi-destination="faqs"]')?.appendChild(node);
          this.querySelector('[data-amc-pi-generic-help]')?.setAttribute('hidden', '');
          faqLocationNote?.remove();
        });
      });

      const headingLevel = (node) => node.nodeType === 1 && /^H[2-4]$/.test(node.tagName) ? Number(node.tagName.slice(1)) : 0;
      sources.forEach((source) => {
        const nodes = Array.from(source.childNodes);
        for (let i = 0; i < nodes.length; i += 1) {
          const heading = nodes[i];
          const level = headingLevel(heading);
          if (!level) continue;
          const text = heading.textContent.trim();
          let kind = '';
          if (/^(?:features|specifications|technical specifications|technical data|electrical(?:\s*&\s*| and )compressor specifications)(?:\s|:|$)/i.test(text)) kind = 'specifications';
          else if (/^(?:dimensions|physical dimensions|product dimensions|overall dimensions|exterior dimensions|interior dimensions|size and dimensions|dimensions and weight|dimensions & weight)(?:\s|:|$)/i.test(text)) kind = 'dimensions';
          else if (faqPattern.test(text)) kind = 'faqs';
          else if (/^(?:resources|resource center|manuals|documents|downloads|free downloads|guides and resources)(?:\s|:|$)/i.test(text)) kind = 'resources';
          if (!kind) continue;
          let end = i + 1;
          while (end < nodes.length && !(headingLevel(nodes[end]) && headingLevel(nodes[end]) <= level)) end += 1;
          const group = nodes.slice(i, end);
          const meaningful = group.slice(1).some((node) => (node.textContent || '').trim() || (node.nodeType === 1 && node.matches('img,video,audio,iframe')));
          const unsafe = group.some((node) => node.nodeType === 1 && !isSafe(node));
          if (!meaningful || unsafe) continue;
          groups.push({ kind, nodes: group });
          i = end - 1;
        }
      });
      groups.forEach((group) => {
        const destination = this.querySelector(`[data-amc-pi-destination="${group.kind}"]`);
        if (!destination) return;
        group.nodes.forEach((node) => destination.appendChild(node));
        if (group.kind === 'faqs') {
          this.querySelector('[data-amc-pi-generic-help]')?.setAttribute('hidden', '');
          faqLocationNote?.remove();
        }
        if (group.kind === 'specifications') this.querySelector('[data-amc-pi-specification-help]')?.setAttribute('hidden', '');
        if (group.kind === 'dimensions') this.querySelector('[data-amc-pi-dimensions-help]')?.setAttribute('hidden', '');
        if (group.kind === 'resources') this.querySelector('[data-amc-pi-resources-help]')?.setAttribute('hidden', '');
      });
    }
  }

  customElements.define('amc-product-information', AMCProductInformation);
})();
